/**
 * Payslip detail — full statutory + non-statutory breakdown, styled like a compact
 * printed payslip (company letterhead, employee details, A5-ish width).
 * GET /payslips/{id} — self-or-(VIEW/MANAGE); GET /payslips/{id}/pdf for the printable version.
 * GET /periods/{id} — only used here to check whether the period is PAID, to decide
 *   whether to show the "paid" stamp + verification QR code.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { companyConfig } from "#/config/company";
import { payrollApi, downloadBlob } from "../api";
import { useAsyncData, getErrorMessage } from "../hooks";
import { monthLabel } from "../types";
import {
  BackBtn, Btn, Card, fmtDate, PageHeader, PayrollError, PayrollLoading,
} from "../components/PayrollUI";

/** Format a "YYYY-MM" period code as "January 2026"; falls back to the raw code
 *  (or the numeric period id) if it isn't in that shape. */
function periodLabel(periodCode?: string | null, periodId?: number) {
  const match = periodCode?.match(/^(\d{4})-(\d{2})$/);
  if (match) {
    const [, year, month] = match;
    return `${monthLabel(Number(month))} ${year}`;
  }
  return periodCode ?? String(periodId ?? "");
}

/** Plain number formatting, no currency symbol — the "Amount (Ksh)" column header
 *  and the summary boxes' labels already say what the unit is. */
function fmtAmount(v?: number | null) {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}

function Row({ label, value, bold }: { label: string; value: React.ReactNode; bold?: boolean }) {
  return (
    <div className={`flex items-baseline gap-3 py-1 text-sm ${bold ? "font-bold text-slate-900" : "text-slate-600"}`}>
      <span className="flex-1">{label}</span>
      <span className={`text-right tabular-nums ${bold ? "text-slate-900" : "text-slate-800"}`}>{value}</span>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-0.5 text-xs font-medium text-slate-800">{value || "—"}</p>
    </div>
  );
}

export default function PayslipDetailPage() {
  const { id } = useParams();
  const payslipId = Number(id);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const { data: p, loading, error, reload } = useAsyncData(() => payrollApi.getPayslip(payslipId), [payslipId]);

  // Only used to check the period's status — is this payslip's period marked PAID? —
  // which decides whether the "paid" stamp and verification QR code render.
  const periodId = p?.periodId;
  const period = useAsyncData(
    () => (periodId ? payrollApi.getPeriod(periodId) : Promise.resolve(null)),
    [periodId],
  );
  const isPaid = period.data?.status === "PAID";

  async function downloadPdf() {
    if (!p) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const blob = await payrollApi.payslipPdf(payslipId);
      downloadBlob(blob, `payslip-${p.employeeNumber}-${p.periodCode ?? p.periodId}.pdf`);
    } catch (err) {
      // Surface the failure instead of letting the button silently stop
      // spinning with no explanation — that's what made this look "broken".
      setPdfError(getErrorMessage(err));
    } finally {
      setPdfLoading(false);
    }
  }

  if (loading) return <PayrollLoading label="Loading payslip…" />;
  if (error) return <PayrollError message={error} onRetry={reload} />;
  if (!p) return <PayrollError message="Payslip not found." />;

  const otherLines = p.lines?.filter((l) => l.lineType === "OTHER") ?? [];
  const benefitLines = p.lines?.filter((l) => l.lineType === "BENEFIT") ?? [];

  // Compact reference encoded in the QR code so a printed copy can be cross-checked against
  // the system — not a cryptographic signature, just a scannable pointer to this exact record.
  const verificationPayload = [
    "EATTA-PAYSLIP",
    `id=${p.id}`,
    `emp=${p.employeeNumber}`,
    `period=${p.periodCode ?? p.periodId}`,
    `net=${p.netPay}`,
  ].join("|");

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Finance · Payslips"
        title={p.employeeName}
        description={`${p.employeeNumber} · ${p.departmentName ?? "—"} · ${periodLabel(p.periodCode, p.periodId)}`}
        actions={
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex gap-2">
              <BackBtn />
              <Btn variant="primary" icon={Download} loading={pdfLoading} loadingText="Preparing…" onClick={downloadPdf}>Download PDF</Btn>
            </div>
            {pdfError && <p className="max-w-xs text-right text-xs text-red-600">{pdfError}</p>}
          </div>
        }
      />

      {/* Constrained to roughly A5 proportions (148 x 210mm) and left-aligned, like a
          compact printed payslip, instead of stretching label/amount across a wide page. */}
      <div className="w-full max-w-[420px] space-y-4">
        <Card noPad>
          {/* Company letterhead — mirrors the PDF's letterhead treatment */}
          <div className="flex items-center gap-3 border-b p-4">
            <img src={companyConfig.logo} alt={companyConfig.name} className="h-10 w-10 shrink-0 rounded object-contain" />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-900">{companyConfig.name}</p>
              <p className="truncate text-[10px] text-slate-500">{companyConfig.location}</p>
              <p className="truncate text-[10px] text-slate-500">{companyConfig.officePhone} · {companyConfig.email}</p>
            </div>
          </div>

          {/* Employee details */}
          <div className="grid grid-cols-3 gap-3 border-b p-4">
            <Field label="Employee Name" value={p.employeeName} />
            <Field label="Employee No." value={p.employeeNumber} />
            <Field label="Job Title" value={p.jobTitle} />
            <Field label="Pay Period" value={periodLabel(p.periodCode, p.periodId)} />
            <Field label="KRA PIN" value={p.employeeKraPin} />
            <Field label="NSSF No." value={p.employeeNssfNumber} />
            <Field label="SHIF No." value={p.employeeNhifNumber} />
          </div>

          <div className="border-t p-4">
            <div className="flex items-baseline gap-3 border-b pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              <span className="flex-1">Computation</span>
              <span>Amount (Ksh)</span>
            </div>
            <Row label="Basic Pay" value={fmtAmount(p.basicPay)} />
            {!!p.houseAllowance && <Row label="House Allowance" value={fmtAmount(p.houseAllowance)} />}
            {!!p.transportAllowance && <Row label="Transport Allowance" value={fmtAmount(p.transportAllowance)} />}
            {!!p.otherAllowance && <Row label="Other Allowance" value={fmtAmount(p.otherAllowance)} />}
            {benefitLines.map((l, i) => <Row key={`benefit-${i}`} label={l.label} value={fmtAmount(l.amount)} />)}
            <div className="mt-1 border-t pt-1">
              <Row label="Gross Pay" value={fmtAmount(p.grossPay)} bold />
            </div>

            <div className="mt-3 border-t pt-2">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Statutory Deductions</p>
              <Row label="NSSF (Employee)" value={fmtAmount(p.nssfEmployee)} />
              <Row label="SHIF" value={fmtAmount(p.shifAmount)} />
              <Row label="Housing Levy (Employee)" value={fmtAmount(p.housingLevyEmployee)} />
              <Row label="Taxable Pay" value={fmtAmount(p.taxablePay)} />
              <Row label="Tax Charged" value={fmtAmount(p.payeCharged)} />
              <Row label="Personal Relief" value={`- ${fmtAmount(p.personalRelief)}`} />
              <Row label="PAYE Payable" value={fmtAmount(p.payePayable)} />
              <div className="mt-1 border-t pt-1">
                <Row label="Statutory Deductions Total" value={fmtAmount(p.statutoryDeductionsTotal)} bold />
              </div>
            </div>

            {otherLines.length > 0 && (
              <div className="mt-3 border-t pt-2">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Other Deductions</p>
                {otherLines.map((l, i) => <Row key={i} label={l.label} value={fmtAmount(l.amount)} />)}
                <div className="mt-1 border-t pt-1">
                  <Row label="Other Deductions Total" value={fmtAmount(p.otherDeductionsTotal)} bold />
                </div>
              </div>
            )}

            <div className="mt-3 border-t-2 border-slate-300 pt-2">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Summary</p>
              <Row label="Gross Pay" value={fmtAmount(p.grossPay)} />
              <Row label="Less Deductions" value={`- ${fmtAmount(p.totalDeductions)}`} />
              <div className="mt-1 border-t pt-1">
                <Row label="Net Pay" value={fmtAmount(p.netPay)} bold />
              </div>
            </div>
          </div>

          {/* Paid stamp + verification QR — only once the period is actually marked PAID */}
          {isPaid && (
            <div className="flex items-center justify-between gap-3 border-t bg-emerald-50/60 p-4">
              <div className="flex items-center gap-2">
                <div className="rotate-[-8deg] rounded-lg border-2 border-emerald-600 px-3 py-1.5">
                  <p className="text-sm font-extrabold uppercase tracking-widest text-emerald-700">Paid</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Payment Date</p>
                  <p className="text-xs font-medium text-slate-700">{fmtDate(period.data?.paidAt)}</p>
                </div>
              </div>
              <div className="shrink-0 rounded-lg border bg-white p-1.5">
                <QRCodeCanvas value={verificationPayload} size={64} level="M" />
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
